/**
 * Shell-mounted connectivity banner (Wave E.5).
 * Shows OfflineBanner while offline; on reconnect announces politely and runs a
 * single lightweight query probe (BAN active-query herd). Does not replace
 * OfflineRecovery (MS-REC-071).
 */

import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { OfflineBanner } from '../shared/ui/OfflineBanner.js';
import { shouldRefetchCustomerOnReconnect } from '../shared/providers/customerQueryClient.js';
import { CUSTOMER_SHELL_WIDTH_CLASS, CUSTOMER_TOP_BAR_CLEARANCE_TOP_CLASS } from './customerTopBarStyles.js';

const RECONNECT_BANNER_MS = 4000;
/** G18 — debounce reconnect probes across rapid online flaps. */
const RECONNECT_PROBE_DEBOUNCE_MS = 2_000;

let lastReconnectProbeAtMs = 0;

function readInitialOffline(): boolean {
  if (typeof navigator === 'undefined') {
    return false;
  }
  return navigator.onLine === false;
}

export function CustomerOfflineBanner(): JSX.Element | null {
  const { t } = useTranslation('customer');
  const queryClient = useQueryClient();
  const [offline, setOffline] = useState(readInitialOffline);
  const [reconnectMessage, setReconnectMessage] = useState<string | null>(null);
  const wasOfflineRef = useRef(offline);

  useEffect(() => {
    const onOffline = (): void => {
      wasOfflineRef.current = true;
      setOffline(true);
      setReconnectMessage(null);
    };
    const onOnline = (): void => {
      setOffline(false);
      if (!wasOfflineRef.current) {
        return;
      }
      wasOfflineRef.current = false;
      setReconnectMessage(t('pwa.onlineAgain'));
      // G18 / Wave D / G8 — storm + guest/network latch skip + debounce + single probe.
      // BAN refetchQueries({ type: 'active' }) herd (RQ refetchOnReconnect handles rest).
      if (!shouldRefetchCustomerOnReconnect()) {
        return;
      }
      const now = Date.now();
      if (now - lastReconnectProbeAtMs < RECONNECT_PROBE_DEBOUNCE_MS) {
        return;
      }
      lastReconnectProbeAtMs = now;
      const active = queryClient.getQueryCache().findAll({ type: 'active' });
      const probe = active[0];
      if (probe !== undefined) {
        void queryClient.refetchQueries({ queryKey: probe.queryKey });
      }
    };

    window.addEventListener('offline', onOffline);
    window.addEventListener('online', onOnline);
    return (): void => {
      window.removeEventListener('offline', onOffline);
      window.removeEventListener('online', onOnline);
    };
  }, [queryClient, t]);

  useEffect(() => {
    if (reconnectMessage === null) {
      return;
    }
    const handle = window.setTimeout(() => {
      setReconnectMessage(null);
    }, RECONNECT_BANNER_MS);
    return (): void => {
      window.clearTimeout(handle);
    };
  }, [reconnectMessage]);

  if (!offline && reconnectMessage === null) {
    return null;
  }

  const message = offline ? t('pwa.offlineBanner') : (reconnectMessage ?? '');

  return (
    <div
      className={`customer-safe-area-x pointer-events-none sticky ${CUSTOMER_TOP_BAR_CLEARANCE_TOP_CLASS} z-30 px-3 pt-2 ${CUSTOMER_SHELL_WIDTH_CLASS} lg:[html[data-sidebar-visible]_&]:pl-56`}
      data-testid="customer-offline-banner-host"
    >
      <div className="pointer-events-auto">
        <OfflineBanner
          message={message}
          testId={offline ? 'customer-offline-banner' : 'customer-offline-reconnect'}
        />
      </div>
    </div>
  );
}
